import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAllRooms } from "../modules/rooms/store";
import { useMessages } from "../graphql/messages";
import { useMarkMessagesAsRead } from "../graphql/useMessages";

export const MessagesContext = React.createContext({
    messages: null,
    loading: null,
    error: null,
    hasUnread: () => null,
    messagesFor: () => null,
    markMessagesAsRead: () => null,
});

const getMessageRef = (message) => `${message?.projectRef}:${message?.id}`;

const applyPendingReadRefs = (serverMessages, pendingReadRefs) => {
    if (!serverMessages) {
        return null;
    }

    const pendingReads = new Set(pendingReadRefs || []);

    return serverMessages.map((message) =>
        pendingReads.has(getMessageRef(message)) && message?.isRead !== true ? { ...message, isRead: true } : message
    );
};

export const MessagesProvider = ({ children }) => {
    const rooms = useSelector(getAllRooms);
    const [pendingReadRefs, setPendingReadRefs] = useState([]);

    const { query: loadMessages, called, loading, error, data: serverMessages, refetch: refetchMessages } = useMessages({
        rooms,
        pollInterval: 60000,
    });
    const { markMessagesAsRead: updateMessagesAsRead, error: setReadError } = useMarkMessagesAsRead();

    useEffect(() => {
        if (!called && rooms && Object.keys(rooms).length > 0) {
            loadMessages();
        }
    }, [rooms]);

    useEffect(() => {
        if (error) {
            console.error(error);
        }
    }, [error]);

    useEffect(() => {
        if (setReadError) {
            console.error(setReadError);
        }
    }, [setReadError]);

    const messages = useMemo(() => applyPendingReadRefs(serverMessages, pendingReadRefs), [serverMessages, pendingReadRefs]);

    const markMessagesAsRead = async ({ projectRef, messageIDs } = {}) => {
        const validMessageIDs = [
            ...new Set((messageIDs || []).filter((messageID) => messageID !== null && messageID !== undefined)),
        ];
        if (!projectRef || validMessageIDs.length === 0 || !serverMessages) {
            return null;
        }

        const messageRefs = serverMessages
            .filter((message) => message?.projectRef === projectRef && validMessageIDs.includes(message?.id))
            .map((message) => getMessageRef(message));
        if (messageRefs.length === 0) {
            return null;
        }

        setPendingReadRefs((currentRefs) => [...new Set([...(currentRefs || []), ...messageRefs])]);

        try {
            const result = await updateMessagesAsRead({
                variables: {
                    projectRef,
                    messageIDs: validMessageIDs,
                },
            });

            if (result?.data?.setReceivedMessagesAsRead !== true) {
                return null;
            }

            if (typeof refetchMessages === "function") {
                await refetchMessages();
            }

            return result;
        } catch (err) {
            return null;
        } finally {
            setPendingReadRefs((currentRefs) => (currentRefs || []).filter((ref) => !messageRefs.includes(ref)));
        }
    };

    const contextValue = useMemo(
        () => ({
            messages,
            loading,
            error,
            hasUnread: (projectRef, roomNumber) => {
                return messages?.some(
                    (m) =>
                        m &&
                        m.isRead !== true &&
                        (!projectRef || m.projectRef === projectRef) &&
                        (!roomNumber || Number(m.roomNumber) === Number(roomNumber))
                );
            },
            messagesFor: (projectRef, roomNumber) => {
                return messages?.filter(
                    (m) =>
                        (!projectRef || m.projectRef === projectRef) &&
                        (!roomNumber || Number(m.roomNumber) === Number(roomNumber))
                );
            },
            markMessagesAsRead,
        }),
        [messages, loading, error, markMessagesAsRead]
    );

    return <MessagesContext.Provider value={contextValue}>{children}</MessagesContext.Provider>;
};
